import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { postApproversServices } from '../services/yclickMaster';
import { postApproverContractOyM, postApproversContracts } from '../services/adminContracts';
import { getMonthlyTeams, postMonthlyApprovers } from '../services/monthlyServiceRequest';
import { groupApprovers } from '../utils/formatUtils';

import useToast from './useToast';

export const approverOriginEnum = {
  servicio: 1,
  equipo: 2,
  contrato: 3
};

const useMonthlyApprovers = ({
  currentApprovers,
  details,
  hideForm,
  fetchApprovers,
  setLocalLoader,
  isOyM
}) => {
  const [optionApprover, setOptionApprover] = useState([]);
  const [teamOptions, setTeamOptions] = useState([]);
  const [teamsByApprover, setTeamsByApprover] = useState(null);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [contractOptions, setContractOptions] = useState([]);

  const { setNewToast } = useToast();

  const { id } = useParams();
  const { values, setFieldValue, resetForm } = useFormik({
    initialValues: {
      approver: null,
      team: null,
      service: null,
      contract: null
    }
  });

  const getTeamsByApprover = (approvers) => {
    // Armo los equipos correspondientes a cada aprobador con aquellos cuyo origenEnum === 2
    const teamOptionsByApprover = {};

    groupApprovers(approvers).forEach((apprList) => {
      const approverTeamOptions = apprList
        .filter(
          (approver) => approver.origenEnum === approverOriginEnum.equipo && approver.origenStr
        )
        .map((appr, index) => ({
          id: index,
          value: index,
          nombre: appr.origenStr,
          label: appr.origenStr
        }));
      const approverId = apprList[0].id;

      teamOptionsByApprover[approverId] = approverTeamOptions;
    });
    setTeamsByApprover(teamOptionsByApprover);
  };

  const formatApproversAndTeams = (approvers) => {
    const formattedApprovers = approvers?.filter(
      (approver, index) => approvers.findIndex((appr) => appr.id === approver.id) === index
    );

    getTeamsByApprover(approvers);

    return formattedApprovers;
  };

  const getApproversByService = async () => {
    try {
      const teamIds = await getMonthlyTeams(id);
      const body = {
        servicioId: details?.servicio?.id,
        listaEquipoIds: teamIds
      };
      const data = await postApproversServices(body);

      return data;
    } catch (_) {
      return [];
    }
  };

  const getApproversByContract = async () => {
    try {
      const body = details?.unidadNegocio?.map((uunn) => ({
        nroContrato: details?.numeroContrato,
        idUnidadNegocio: uunn.id
      }));
      const response = await postApproversContracts(body);

      return response?.data?.aprobadores;
    } catch (_) {
      return [];
    }
  };

  const getApproversbyContractOyM = async () => {
    try {
      const body = details?.operacion?.map((operacion) => ({
        nroContrato: details?.numeroContrato,
        idOperacion: operacion.id
      }));

      const response = await postApproverContractOyM(body);

      return response?.data?.aprobadores;
    } catch (_) {
      return [];
    }
  };

  const getApprovers = async () => {
    setLocalLoader(true);
    let approvers = [];

    if (details?.numeroContrato && details?.unidadNegocio?.length) {
      const approversByContract = await getApproversByContract();

      approvers = [...approvers, ...approversByContract];
    }

    if (details?.servicio?.id && !isOyM) {
      const approversByService = await getApproversByService();

      approvers = [...approvers, ...approversByService];
    }

    if (details?.operacion?.length) {
      const approversbyContractOyM = await getApproversbyContractOyM();

      approvers = [...approvers, ...approversbyContractOyM];
    }
    approvers = formatApproversAndTeams(approvers);
    setOptionApprover(approvers);
    setLocalLoader(false);
  };

  const getServiceOptions = () => {
    const singleService = {
      id: details?.servicio?.id,
      value: details?.servicio?.id,
      label: details?.servicio?.nombre,
      nombre: details?.servicio?.nombre
    };

    setServiceOptions([singleService]);
  };

  const getContractOptions = () => {
    const singleContract = {
      id: 1,
      value: 1,
      nombre: details?.numeroContrato,
      label: details?.numeroContrato
    };

    setContractOptions([singleContract]);
  };
  const approverExists = () =>
    currentApprovers.some(
      (approver) =>
        approver.nombre === values.approver?.nombre &&
        approver.equipoNombre === (values.team?.nombre || null) &&
        approver.servicioNombre === (values.service?.nombre || null) &&
        approver.contratoNumero === (Number(values.contract?.nombre) || null)
    );
  const addApprover = async () => {
    setLocalLoader(true);
    try {
      const exist = approverExists();

      if (exist) {
        throw new Error('El aprobador ya existe');
      }
      const body = {
        idSolicitudServicioMensualizada: id,
        nombre: values.approver.nombre,
        idAprobador: values.approver.id,
        esGrupo: values.approver.esGrupo,
        equipoNombre: values.team?.nombre || null,
        servicioNombre: values.service?.nombre || null,
        contratoNumero: values.contract?.nombre || null
      };

      await postMonthlyApprovers(body);
      await fetchApprovers(true);
      hideForm();
    } catch (err) {
      setNewToast({
        type: 'ERROR',
        message: err.message
      });
    }
    setLocalLoader(false);
  };

  useEffect(() => {
    getApprovers();
    getServiceOptions();
    getContractOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details]);

  useEffect(() => {
    if (values.approver) {
      setTeamOptions(teamsByApprover[values.approver?.id]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.approver]);

  return {
    optionApprover,
    teamOptions,
    serviceOptions,
    contractOptions,
    values,
    setFieldValue,
    resetForm,
    addApprover
  };
};

export default useMonthlyApprovers;
